import {
  CS_USERS,
  MANAGER_TYPE_OPTIONS,
  ROLE_FILTER_CONFIG,
  USER_ROLES,
  USER_TYPES,
} from "./userConfigs";

export const CUSTOMER_SUPPORT_ROLE = "cs_user";

export const isAlphanumeric = (value) => {
  const pattern = /^[a-z0-9]+$/i;
  return pattern.test(value);
};

export const isValidUsername = (value) => {
  const pattern = /^[a-z0-9@#$&\-._]+$/i;
  return pattern.test(value);
};

export const isCustomerSupportRole = (userType) => {
  return userType === CUSTOMER_SUPPORT_ROLE || CS_USERS.includes(userType);
};

export const getDisplayUserType = (userType) => {
  return CS_USERS.includes(userType) ? CUSTOMER_SUPPORT_ROLE : userType;
};

export const getSubmittedUserType = (userType, supportLevel) => {
  if (userType === CUSTOMER_SUPPORT_ROLE && supportLevel) {
    return supportLevel;
  }

  return userType;
};

export const shouldShowManagerType = (userType) => {
  return (
    userType === USER_TYPES.TERRITORY_MANAGER ||
    userType === USER_TYPES.AGENCY_USER
  );
};

export const shouldShowSupportLevel = (userType) => {
  return isCustomerSupportRole(userType);
};

export const shouldShowOperationsManagerFields = (userType) => {
  return userType === "agency_user";
};

export const getAssignmentFieldName = (userType) => {
  const fieldMapping = {
    [USER_TYPES.CIRCLE_HEAD]: "cluster",
    [USER_TYPES.REGIONAL_MANAGER]: "region",
    [USER_TYPES.AREA_MANAGER]: "area",
    [USER_TYPES.TERRITORY_MANAGER]: "territory",
    [USER_TYPES.AGENCY_USER]: "agency",
  };
  return fieldMapping[userType];
};

export const getTaggingText = (tags) =>
  Array.isArray(tags) && tags.length ? tags.join(", ") : "-";

export const validateRequired = (value, fieldName) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return `${fieldName} is required`;
    }
    return null;
  }

  if (!value || (typeof value === "string" && value.trim() === "")) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateEmail = (email) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return "Please enter a valid email address";
  }
  return null;
};

export const validatePhoneNumber = (
  phoneNumber,
  minLength = 10,
  maxLength = 14
) => {
  const cleanNumber = phoneNumber.trim();
  if (minLength === maxLength && cleanNumber.length !== minLength) {
    return `Phone number must be ${minLength} digits`;
  }
  if (cleanNumber.length < minLength || cleanNumber.length > maxLength) {
    return `Phone number must be between ${minLength} and ${maxLength} digits`;
  }
  return null;
};

export const hasPhoneDigits = (phoneNumber) => {
  if (typeof phoneNumber !== "string") {
    return false;
  }

  const cleanNumber = phoneNumber.replace(/\D/g, "");
  if (cleanNumber.startsWith("88")) {
    return cleanNumber.slice(2).length > 0;
  }

  return cleanNumber.length > 0;
};

export const validatePassword = (password) => {
  if (password.length < 8 || password.length > 50) {
    return "Password must be between 8 and 50 characters";
  }
  return null;
};

export const validatePasswordMatch = (password, confirmPassword) => {
  if (password !== confirmPassword) {
    return "Passwords do not match";
  }
  return null;
};

export const validateStringLength = (value, fieldName, maxLength) => {
  if (value && value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters`;
  }
  return null;
};

export const validateUsername = (username) => {
  if (!isValidUsername(username)) {
    return "Username can only contain alphanumeric characters and @, #, $, &, -, .";
  }
  return validateStringLength(username, "Username", 50);
};

export const validateImage = (image) => {
  if (!image) return null;

  const maxSize = 1 * 1024 * 1024;
  const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  if (image.size > maxSize) {
    return "Image size must be less than 1MB";
  }

  if (!allowedTypes.includes(image.type)) {
    return "Image must be in JPEG, PNG, GIF, or WebP format";
  }

  return null;
};

export const hasRequiredUserFormFields = (form, options = {}) => {
  const { isEditMode = false } = options;
  const assignmentField = getAssignmentFieldName(form.user_type);
  const requiredFields = [
    { field: "username", name: "Username" },
    { field: "name", name: "Full Name" },
    { field: "contact_number", name: "Phone Number" },
    { field: "email", name: "Email Address" },
    { field: "joining_date", name: "Joining Date" },
    { field: "user_type", name: "Role" },
  ];

  if (!isEditMode) {
    requiredFields.push(
      { field: "password", name: "Password" },
      { field: "confirm_password", name: "Confirm Password" }
    );
  }

  if (assignmentField) {
    requiredFields.push({ field: assignmentField, name: "Assigned to" });
  }

  if (shouldShowManagerType(form.user_type)) {
    requiredFields.push({ field: "user_category", name: "Manager Type" });
  }

  if (shouldShowSupportLevel(form.user_type)) {
    requiredFields.push({ field: "support_level", name: "Support Level" });
  }

  if (shouldShowOperationsManagerFields(form.user_type)) {
    requiredFields.push(
      { field: "leave_day", name: "Leave Days" },
      { field: "verification_type", name: "Verification Type" },
      { field: "verification_id", name: "Verification ID" }
    );
  }

  return requiredFields.every(
    ({ field, name }) => !validateRequired(form[field], name)
  );
};

export const validateUserForm = (form, options = {}) => {
  const { isEditMode = false } = options;
  const errors = {};
  const assignmentField = getAssignmentFieldName(form.user_type);
  const requiredFields = [
    { field: "username", name: "Username" },
    { field: "name", name: "Full Name" },
    { field: "contact_number", name: "Phone Number" },
    { field: "email", name: "Email Address" },
    { field: "joining_date", name: "Joining Date" },
    { field: "user_type", name: "Role" },
  ];

  if (!isEditMode) {
    requiredFields.push(
      { field: "password", name: "Password" },
      { field: "confirm_password", name: "Confirm Password" }
    );
  }

  if (assignmentField) {
    requiredFields.push({ field: assignmentField, name: "Assigned to" });
  }

  requiredFields.forEach(({ field, name }) => {
    const error = validateRequired(form[field], name);
    if (error) {
      if (!errors[field]) errors[field] = [];
      errors[field].push(error);
    }
  });

  if (shouldShowManagerType(form.user_type)) {
    const error = validateRequired(form.user_category, "Manager Type");
    if (error) {
      if (!errors.user_category) errors.user_category = [];
      errors.user_category.push(error);
    }
  }

  if (shouldShowSupportLevel(form.user_type)) {
    const error = validateRequired(form.support_level, "Support Level");
    if (error) {
      if (!errors.support_level) errors.support_level = [];
      errors.support_level.push(error);
    }
  }

  if (shouldShowOperationsManagerFields(form.user_type)) {
    const verificationFieldLabel = getVerificationIdFieldLabel(
      form.verification_type
    );

    const operationsFields = [
      { field: "leave_day", name: "Leave Days" },
      { field: "verification_type", name: "Verification Type" },
      { field: "verification_id", name: verificationFieldLabel },
    ];

    operationsFields.forEach(({ field, name }) => {
      const error = validateRequired(form[field], name);
      if (error) {
        if (!errors[field]) errors[field] = [];
        errors[field].push(error);
      }
    });
  }

  if (form.email) {
    const emailError = validateEmail(form.email);
    if (emailError) {
      if (!errors.email) errors.email = [];
      errors.email.push(emailError);
    }
  }

  if (form.contact_number) {
    const phoneError = validatePhoneNumber(form.contact_number, 14, 14);
    if (phoneError) {
      if (!errors.contact_number) errors.contact_number = [];
      errors.contact_number.push(phoneError);
    }
  }

  if (hasPhoneDigits(form.personal_number)) {
    const bkashError = validatePhoneNumber(form.personal_number, 14, 14);
    if (bkashError) {
      if (!errors.personal_number) errors.personal_number = [];
      errors.personal_number.push(bkashError);
    }
  }

  if (form.username) {
    const usernameError = validateUsername(form.username);
    if (usernameError) {
      if (!errors.username) errors.username = [];
      errors.username.push(usernameError);
    }
  }

  if (form.password) {
    const passwordError = validatePassword(form.password);
    if (passwordError) {
      if (!errors.password) errors.password = [];
      errors.password.push(passwordError);
    }
  }

  if (isEditMode && form.confirm_password && !form.password) {
    if (!errors.password) errors.password = [];
    errors.password.push(
      "Password is required when confirm password is entered"
    );
  }

  if (isEditMode && form.password && !form.confirm_password) {
    if (!errors.confirm_password) errors.confirm_password = [];
    errors.confirm_password.push(
      "Confirm Password is required when password is entered"
    );
  }

  if (form.password && form.confirm_password) {
    const matchError = validatePasswordMatch(
      form.password,
      form.confirm_password
    );
    if (matchError) {
      if (!errors.confirm_password) errors.confirm_password = [];
      errors.confirm_password.push(matchError);
    }
  }

  const verificationFieldLabel = getVerificationIdFieldLabel(
    form.verification_type
  );

  const stringLengthFields = [
    { field: "father_name", name: "Father's Name", maxLength: 50 },
    { field: "mother_name", name: "Mother's Name", maxLength: 50 },
    { field: "verification_id", name: verificationFieldLabel, maxLength: 50 },
  ];

  stringLengthFields.forEach(({ field, name, maxLength }) => {
    if (form[field]) {
      const error = validateStringLength(form[field], name, maxLength);
      if (error) {
        if (!errors[field]) errors[field] = [];
        errors[field].push(error);
      }
    }
  });
  if (form.image instanceof Blob) {
    const imageError = validateImage(form.image);
    if (imageError) {
      if (!errors.image) errors.image = [];
      errors.image.push(imageError);
    }
  }
  return errors;
};

export const formatPhoneNumber = (value) => {
  const maxDigits = 11;
  const prefix = "+88";

  const rawValue = typeof value === "string" ? value : "";
  if (rawValue.length < prefix.length) {
    return prefix;
  }

  const digitsOnly = rawValue.startsWith(prefix)
    ? rawValue.slice(prefix.length).replace(/\D/g, "")
    : rawValue.replace(/\D/g, "");

  return prefix + digitsOnly.slice(0, maxDigits);
};

export const formatEmail = (value) => {
  const email = value.toLowerCase().trim();
  return formatString(email, 50);
};

export const formatToAlphanumericOrSpecialCharacters = (
  value,
  { minLength = 0, maxLength = 50 } = {}
) => {
  const alphanumeric = value.replace(/[^a-zA-Z0-9@#$&\-._]/g, "");
  return formatString(alphanumeric, maxLength);
};

export const formatPassword = (value) => {
  const formatted = value.replace(/\s/g, "");
  return formatString(formatted, 50);
};

export const formatString = (value, maxLength) => {
  return value.slice(0, maxLength);
};

export const formatEmployeeId = (value) => {
  const employeeId = value.replace(/[^a-zA-Z0-9]/g, "");
  return formatString(employeeId, 50);
};

export const getManagerTypeOptions = (userType) => {
  return MANAGER_TYPE_OPTIONS[userType] || [];
};

export const getRoleOptions = (roles) => {
  return [
    { label: "Select User Role", value: "", isPlaceholder: true },
    ...roles.map((role) => ({
      label: role.name,
      value: role.value,
    })),
  ];
};

export const getCreateEditRoleOptions = (roles) => {
  const hasCustomerSupportRole = roles.some((role) =>
    CS_USERS.includes(role.value)
  );

  const nonCustomerSupportRoles = roles.filter(
    (role) => !CS_USERS.includes(role.value)
  );

  return [
    ...nonCustomerSupportRoles,
    ...(hasCustomerSupportRole
      ? [
          {
            name: "Customer Support",
            value: CUSTOMER_SUPPORT_ROLE,
          },
        ]
      : []),
  ];
};

export const getRoleFilterConfig = (loggedInUserRole) => {
  const config = ROLE_FILTER_CONFIG[loggedInUserRole] || {
    defaultRole: null,
    visibleRoles: [],
  };
  const roleMap = new Map(USER_ROLES.map((r) => [r.value, r]));

  const filterOptions = config.visibleRoles
    .map((value) => roleMap.get(value))
    .filter(Boolean);

  return {
    filterOptions,
    defaultValue: config.defaultRole || "",
  };
};

export const getAssignmentFieldLabel = (userType) => {
  const labelMapping = {
    [USER_TYPES.CIRCLE_HEAD]: "Cluster",
    [USER_TYPES.REGIONAL_MANAGER]: "Region",
    [USER_TYPES.AREA_MANAGER]: "Area",
    [USER_TYPES.TERRITORY_MANAGER]: "Territory",
    [USER_TYPES.AGENCY_USER]: "Agency",
  };
  return labelMapping[userType] || "Assigned to";
};

export const getVerificationIdFieldLabel = (verificationType) => {
  const labelMapping = {
    NID: "NID Number",
    passport: "Passport Number",
    drivers_license: "Driver's License Number",
    birth_certificate: "Birth Certificate Number",
  };
  return labelMapping[verificationType] || "Verification ID";
};
